import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, KeyRound, User as UserIcon, Tags, Sliders, Eye, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';

const SHOW_OPTS = [
  { value: 'always', label: 'Altid' },
  { value: 'never', label: 'Aldrig' },
  { value: 'ask', label: 'Spørg hver gang' },
];

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [savingCats, setSavingCats] = useState(false);
  const [categories, setCategories] = useState([]);

  const [profile, setProfile] = useState({
    email: user?.email || '',
    nickname: user?.nickname || '',
    show_video: user?.show_video || 'ask',
    show_nickname: user?.show_nickname || 'ask',
    categories: user?.categories || [],
  });

  const [pwd, setPwd] = useState({ old_password: '', new_password: '', new_password_confirm: '' });
  const [emailConfirm, setEmailConfirm] = useState(user?.email || '');

  useEffect(() => {
    api.get('/categories').then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const toggleCategory = (id) => {
    setProfile((p) => ({
      ...p,
      categories: p.categories.includes(id) ? p.categories.filter((c) => c !== id) : [...p.categories, id],
    }));
  };

  const patchProfile = async (payload, setter) => {
    setter(true);
    try {
      const { data } = await api.put('/users/me', payload);
      setUser(data);
      toast.success('Profil opdateret');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fejl ved opdatering');
    } finally {
      setter(false);
    }
  };

  const saveStamdata = (e) => {
    e.preventDefault();
    const newEmail = (profile.email || '').trim().toLowerCase();
    const confirmEmail = (emailConfirm || '').trim().toLowerCase();
    if (newEmail !== (user?.email || '').toLowerCase()) {
      if (newEmail !== confirmEmail) {
        toast.error('E-mail-adresserne stemmer ikke overens. Bekræft venligst den nye adresse.');
        return;
      }
    }
    patchProfile({ email: profile.email }, setSaving);
  };

  const savePrefs = (e) => {
    e.preventDefault();
    patchProfile({
      show_video: profile.show_video,
      show_nickname: profile.show_nickname,
    }, setSavingPrefs);
  };

  const saveCategories = (e) => {
    e.preventDefault();
    patchProfile({ categories: profile.categories }, setSavingCats);
  };

  const [pwdOldVisible, setPwdOldVisible] = useState(false);
  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.new_password_confirm) {
      toast.error('De to nye adgangskoder er ikke ens');
      return;
    }
    setPwdSaving(true);
    try {
      await api.post('/users/change-password', { old_password: pwd.old_password, new_password: pwd.new_password });
      toast.success('Adgangskode ændret');
      setPwd({ old_password: '', new_password: '', new_password_confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fejl ved skift af adgangskode');
    } finally {
      setPwdSaving(false);
    }
  };

  const peekOldPassword = () => {
    setPwdOldVisible(true);
    setTimeout(() => setPwdOldVisible(false), 2000);
  };

  return (
    <div className="pl-4 pr-6 md:pl-6 md:pr-12 py-10 md:py-16 pb-24 md:pb-16 max-w-5xl">
      <div className="mb-10 animate-fade-up">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Din konto</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Profil</h1>
      </div>

      <Tabs defaultValue="stamdata">
        <TabsList className="bg-[#18181B] border border-border">
          <TabsTrigger value="stamdata" data-testid="profile-tab-stamdata">
            <UserIcon className="w-4 h-4 mr-2" /> Stamdata
          </TabsTrigger>
          <TabsTrigger value="prefs" data-testid="profile-tab-prefs">
            <Sliders className="w-4 h-4 mr-2" /> Præferencer
          </TabsTrigger>
          <TabsTrigger value="cats" data-testid="profile-tab-cats">
            <Tags className="w-4 h-4 mr-2" /> Kategorier
          </TabsTrigger>
        </TabsList>

        {/* Stamdata */}
        <TabsContent value="stamdata" className="mt-6 space-y-8">
          <form onSubmit={saveStamdata} className="space-y-4 rounded-lg border border-border bg-[#18181B] py-6 pl-4 pr-6 md:py-8 md:pl-6 md:pr-8">
            <h2 className="font-display text-2xl font-bold tracking-tight mb-4">Stamdata</h2>

            <div className="grid grid-cols-[140px_1fr] items-center gap-x-4 gap-y-3">
              <Label className="text-left text-xs font-medium text-muted-foreground flex items-center gap-1"><Lock className="w-3 h-3" /> Kaldenavn</Label>
              <Input
                value={profile.nickname}
                readOnly
                disabled
                className="w-full bg-[#0a0a0c] border-border text-muted-foreground cursor-not-allowed"
                data-testid="profile-nickname-input"
              />

              <div />
              <p className="text-xs text-muted-foreground -mt-1">Kaldenavn kan ikke ændres efter oprettelse.</p>

              <Label className="text-left text-xs font-medium text-muted-foreground">Email</Label>
              <Input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className="w-full bg-[#09090B] border-border" data-testid="profile-email-input" />

              <Label className="text-left text-xs font-medium text-muted-foreground">Bekræft email</Label>
              <Input
                type="email"
                value={emailConfirm}
                onChange={(e) => setEmailConfirm(e.target.value)}
                className="w-full bg-[#09090B] border-border"
                placeholder="Skriv email igen for at bekræfte"
                data-testid="profile-email-confirm-input"
              />
            </div>

            <Button type="submit" disabled={saving}
              className="bg-volt text-black hover:bg-volt hover:-translate-y-[1px] hover:shadow-lg transition-all font-semibold mt-4"
              data-testid="profile-save-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Gemmer…' : 'Gem stamdata'}
            </Button>
          </form>

          <form onSubmit={changePassword} className="space-y-4 rounded-lg border border-border bg-[#18181B] py-6 pl-4 pr-6 md:py-8 md:pl-6 md:pr-8">
            <div className="flex items-center gap-3 mb-4">
              <KeyRound className="w-5 h-5 text-volt" />
              <h2 className="font-display text-2xl font-bold tracking-tight">Skift adgangskode</h2>
            </div>

            <div className="grid grid-cols-[140px_1fr] items-center gap-x-4 gap-y-3">
              <Label className="text-left text-xs font-medium text-muted-foreground">Nuværende adgangskode</Label>
              <div className="relative">
                <Input
                  type={pwdOldVisible ? 'text' : 'password'}
                  value={pwd.old_password}
                  onChange={(e) => setPwd({ ...pwd, old_password: e.target.value })}
                  className="w-full bg-[#09090B] border-border pr-10"
                  required
                  data-testid="profile-oldpwd-input"
                />
                <button
                  type="button"
                  onClick={peekOldPassword}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-volt transition-colors"
                  title="Vis i 2 sekunder"
                  data-testid="profile-oldpwd-peek-btn"
                >
                  <Eye className={`w-4 h-4 ${pwdOldVisible ? 'text-volt' : ''}`} />
                </button>
              </div>

              <Label className="text-left text-xs font-medium text-muted-foreground">Ny adgangskode</Label>
              <Input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
                className="w-full bg-[#09090B] border-border" required minLength={6} data-testid="profile-newpwd-input" />

              <Label className="text-left text-xs font-medium text-muted-foreground">Bekræft ny adgangskode</Label>
              <Input type="password" value={pwd.new_password_confirm} onChange={(e) => setPwd({ ...pwd, new_password_confirm: e.target.value })}
                className="w-full bg-[#09090B] border-border" required minLength={6} data-testid="profile-newpwd-confirm-input" />
            </div>

            <Button type="submit" disabled={pwdSaving}
              className="bg-volt text-black hover:bg-volt hover:-translate-y-[1px] hover:shadow-lg transition-all font-semibold mt-4"
              data-testid="profile-change-pwd-btn">
              {pwdSaving ? 'Skifter…' : 'Skift adgangskode'}
            </Button>
          </form>
        </TabsContent>

        {/* Præferencer */}
        <TabsContent value="prefs" className="mt-6">
          <form onSubmit={savePrefs} className="space-y-6 rounded-lg border border-border bg-[#18181B] py-6 pl-4 pr-6 md:py-8 md:pl-6 md:pr-8">
            <h2 className="font-display text-2xl font-bold tracking-tight">Videopræferencer</h2>

            <div className="grid grid-cols-[140px_1fr] items-center gap-x-4 gap-y-3">
              <Label className="text-left text-xs font-medium text-muted-foreground">Vis video</Label>
              <Select value={profile.show_video} onValueChange={(v) => setProfile({ ...profile, show_video: v })}>
                <SelectTrigger className="w-full bg-[#09090B] border-border" data-testid="profile-show-video-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181B] border-border">
                  {SHOW_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>

              <Label className="text-left text-xs font-medium text-muted-foreground">Vis kaldenavn</Label>
              <Select value={profile.show_nickname} onValueChange={(v) => setProfile({ ...profile, show_nickname: v })}>
                <SelectTrigger className="w-full bg-[#09090B] border-border" data-testid="profile-show-nickname-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181B] border-border">
                  {SHOW_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" disabled={savingPrefs}
              className="bg-volt text-black hover:bg-volt hover:-translate-y-[1px] hover:shadow-lg transition-all font-semibold"
              data-testid="profile-save-prefs-btn">
              <Save className="w-4 h-4 mr-2" /> {savingPrefs ? 'Gemmer…' : 'Gem præferencer'}
            </Button>
          </form>
        </TabsContent>

        {/* Kategorier */}
        <TabsContent value="cats" className="mt-6">
          <form onSubmit={saveCategories} className="space-y-6 rounded-lg border border-border bg-[#18181B] py-6 pl-4 pr-6 md:py-8 md:pl-6 md:pr-8">
            <div className="flex items-center gap-3">
              <Tags className="w-5 h-5 text-volt" />
              <h2 className="font-display text-2xl font-bold tracking-tight">Kategorier du er interesseret i</h2>
            </div>

            <div className="grid grid-cols-[140px_1fr] items-start gap-x-4 gap-y-3">
              <Label className="text-left text-xs font-medium text-muted-foreground pt-2">Interesser</Label>
              {categories.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ingen kategorier oprettet endnu. Administrator kan oprette dem via Admin-panelet.</p>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="profile-categories-list">
                  {categories.map((c) => {
                    const selected = profile.categories.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => toggleCategory(c.id)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${selected ? 'bg-volt text-black border-volt' : 'bg-[#09090B] text-muted-foreground border-border hover:border-volt/40 hover:text-white'}`}
                        data-testid={`category-toggle-${c.id}`}
                      >
                        {c.category || c.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <Button type="submit" disabled={savingCats || categories.length === 0}
              className="bg-volt text-black hover:bg-volt hover:-translate-y-[1px] hover:shadow-lg transition-all font-semibold"
              data-testid="profile-save-cats-btn">
              <Save className="w-4 h-4 mr-2" /> {savingCats ? 'Gemmer…' : 'Gem kategorier'}
            </Button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
}
